import { useMemo } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Button,
  Card,
  Title3,
  Text,
} from '@fluentui/react-components';
import {
  CheckmarkCircleRegular,
  VideoRegular,
} from '@fluentui/react-icons';
import type { BookingResponse } from '../types/booking.types';

interface BookingConfirmationProps {
  booking: BookingResponse;
  timezone: string;
}

const useStyles = makeStyles({
  card: {
    padding: tokens.spacingHorizontalXXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    textAlign: 'center',
  },
  checkIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalSNudge,
    alignItems: 'center',
    marginTop: tokens.spacingVerticalS,
  },
  detailLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  detailValue: {
    fontWeight: 600,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalS,
  },
});

export function BookingConfirmation({
  booking,
  timezone,
}: BookingConfirmationProps) {
  const styles = useStyles();

  const dateTimeStr = useMemo(() => {
    if (!booking.slotStart) return '';
    const formatter = new Intl.DateTimeFormat('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone || 'Australia/Sydney',
    });
    return formatter.format(new Date(booking.slotStart));
  }, [booking.slotStart, timezone]);

  return (
    <Card className={styles.card}>
      <CheckmarkCircleRegular
        fontSize={56}
        className={styles.checkIcon}
      />

      <Title3>Meeting booked!</Title3>

      <div className={styles.details}>
        <Text className={styles.detailLabel}>When</Text>
        <Text className={styles.detailValue}>{dateTimeStr}</Text>

        <Text className={styles.detailLabel}>With</Text>
        <Text className={styles.detailValue}>{booking.staffName}</Text>
      </div>

      <div className={styles.actions}>
        {booking.teamsJoinUrl && (
          <Button
            appearance="primary"
            size="large"
            icon={<VideoRegular />}
            as="a"
            href={booking.teamsJoinUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Join on Teams
          </Button>
        )}

        <Button
          appearance="subtle"
          size="large"
          as="a"
          href="/"
        >
          Back to Home
        </Button>
      </div>

      <Body1 className={styles.note}>
        You'll receive a calendar invite shortly — accept it in Outlook to add
        the meeting to your calendar.
      </Body1>
    </Card>
  );
}
